import React from "react";
import debounce from "debounce";

import { Alert, Button, Drawer, Form, Input, InputNumber, Spin, Tag, message } from "antd";

import { _getTermContractProductPreview, _postTermContractLine } from "../../services";
import { BackingSellerSelect } from "./selects";
import { BACKING_SUPPLIER_LABEL, errorMessage, formatPct, marginPct, pctVsList, toNumberOrNull } from "./helpers";

const MUTED = "rgba(0,0,0,0.45)";
const EMPTY = { productId: "", preview: null, previewError: null, loadingPreview: false, price: undefined, costPrice: undefined, backingSellerId: null };

const PreviewCard = ({ preview, enterpriseName }) => (
    <div style={{ display: "flex", gap: 14, padding: 14, border: "1px solid #e8e8e8", borderRadius: 6, marginBottom: 16 }}>
        {preview.image
            ? <img src={preview.image} alt="" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 4, flexShrink: 0 }} />
            : <div style={{ width: 72, height: 72, borderRadius: 4, background: "#f5f5f5", flexShrink: 0 }} />}
        <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{preview.title}</div>
            <div style={{ fontSize: 12, color: MUTED }}>
                {preview.sku ? `SKU ${preview.sku} · ` : ""}Listed by {preview.listingSupplierName || "-"}
            </div>
            <div>List price <b>{preview.listPrice === null ? "-" : preview.listPrice}</b> {preview.currency}</div>
            <div style={{ marginTop: 4 }}>
                {preview.inPrivateCatalog === true && <Tag color="green">In {enterpriseName}'s private catalog</Tag>}
                {preview.inPrivateCatalog === false && <Tag color="orange">Not in {enterpriseName}'s private catalog</Tag>}
                {preview.inPrivateCatalog === null && <Tag>Private catalog could not be checked</Tag>}
                {preview.activeVipPrice && (
                    <Tag color="orange">Has VIP price {preview.activeVipPrice.discountPrice} (contract price wins)</Tag>
                )}
            </div>
        </div>
    </div>
);

/** Paste a product ID → preview → prices with live discount and margin → backing supplier → Add, or Add & next. */
class AddLineDrawer extends React.Component {

    state = { ...EMPTY, saving: false };

    componentWillUnmount() {
        this.loadPreview.clear();
        this.unmounted = true;
    }

    loadPreview = debounce(async ({
        productId,
    }) => {
        /* a slower, older response must not overwrite the preview of what is in the box now */
        const isCurrent = () => !this.unmounted && this.state.productId.trim() === productId;
        try {
            const { data } = await _getTermContractProductPreview({ id: this.props.contract._id, productId });
            if (isCurrent()) this.setState({ preview: data.preview, previewError: null, loadingPreview: false });
        } catch (e) {
            if (isCurrent()) this.setState({ preview: null, previewError: errorMessage({ error: e }), loadingPreview: false });
        }
    }, 400);

    onProductId = ({
        productId,
    }) => {
        const trimmed = productId.trim();
        this.setState({ productId, preview: null, previewError: null, loadingPreview: !!trimmed });
        if (trimmed) this.loadPreview({ productId: trimmed });
    };

    add = async ({
        next,
    }) => {
        const { contract, onAdded, onClose } = this.props;
        const { productId, price, costPrice, backingSellerId } = this.state;
        this.setState({ saving: true });
        try {
            await _postTermContractLine({
                id: contract._id,
                data: {
                    productId: productId.trim(),
                    price: toNumberOrNull({ value: price }),
                    costPrice: toNumberOrNull({ value: costPrice }),
                    backingSellerId: backingSellerId || null
                }
            });
            message.success("Line added");
            onAdded();
            if (!next) {
                onClose();
                return;
            }
            this.setState({ ...EMPTY, saving: false }, () => this.productInput && this.productInput.focus());
        } catch (e) {
            message.error(errorMessage({ error: e }));
            this.setState({ saving: false });
        }
    };

    render() {
        const { contract, enterpriseName, onClose } = this.props;
        const { productId, preview, previewError, loadingPreview, price, costPrice, backingSellerId, saving } = this.state;
        const margin = marginPct({ price, costPrice });
        const discount = preview ? pctVsList({ price, listPrice: preview.listPrice }) : "";
        const canAdd = preview && !preview.alreadyOnLineId && typeof price === "number" && !saving;
        return (
            <Drawer
                visible
                width={520}
                title={`Add line to ${contract.reference}`}
                onClose={onClose}
                bodyStyle={{ paddingBottom: 80 }}
            >
                <Form layout="vertical">
                    <Form.Item label="Product ID" required help="Paste the marketplace product ID. The product loads as you paste.">
                        <Input
                            autoFocus
                            ref={input => { this.productInput = input; }}
                            value={productId}
                            onChange={e => this.onProductId({ productId: e.target.value })}
                        />
                    </Form.Item>
                    {loadingPreview && <Spin style={{ display: "block", marginBottom: 16 }} />}
                    {previewError && <Alert type="error" message={previewError} style={{ marginBottom: 16 }} />}
                    {preview && <PreviewCard preview={preview} enterpriseName={enterpriseName} />}
                    {preview && preview.alreadyOnLineId && (
                        <Alert type="warning" message="This product is already a line on this contract. Edit that line instead." style={{ marginBottom: 16 }} />
                    )}
                    <div style={{ display: "flex", gap: 16 }}>
                        <Form.Item label="Contract price" required style={{ flex: 1 }}
                            help={`What ${enterpriseName || "the enterprise"} pays${discount ? ` · ${discount}` : ""}`}>
                            <InputNumber min={0} value={price} style={{ width: "100%" }} onChange={value => this.setState({ price: value })} />
                        </Form.Item>
                        <Form.Item label="Cost price" style={{ flex: 1 }}
                            help={<span style={{ color: margin !== null && margin < 0 ? "#a8071a" : undefined }}>Margin {formatPct({ value: margin })} · admin/ERP only</span>}>
                            <InputNumber min={0} value={costPrice} style={{ width: "100%" }} onChange={value => this.setState({ costPrice: value })} />
                        </Form.Item>
                    </div>
                    <Form.Item label={BACKING_SUPPLIER_LABEL} help="Optional. The real supplier in IST, not the listing (ETH) account.">
                        <BackingSellerSelect value={backingSellerId} onChange={value => this.setState({ backingSellerId: value })} />
                    </Form.Item>
                </Form>
                <div style={{
                    position: "absolute", left: 0, right: 0, bottom: 0, padding: "10px 16px",
                    borderTop: "1px solid #e8e8e8", background: "#fff", textAlign: "right"
                }}>
                    <Button onClick={onClose}>Cancel</Button>{" "}
                    <Button disabled={!canAdd} onClick={() => this.add({ next: true })}>Add &amp; next</Button>{" "}
                    <Button type="primary" disabled={!canAdd} loading={saving} onClick={() => this.add({ next: false })}>Add line</Button>
                </div>
            </Drawer>
        );
    }
}

export default AddLineDrawer;



// WEBPACK FOOTER //
// ./src/components/term-contract/add-line-drawer.js